import { api } from '@/shared/axios-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CompareResponse } from './compareBuildDiff';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TestPlan {
  id: number;
  name: string;
}

export interface TestSuite {
  id: number;
  name: string;
}

export interface StatusResponse {
  status: 'processing' | 'completed' | 'failed';
}

export interface AdoTestCase {
  testCaseId: number;
  title: string;
  similarity: number;
  steps: string;
  confidence: string;
  priority: string;
}

interface CoveredSuggestedTestCase extends AdoTestCase {
  type?: string;
  module?: string;
  preconditions?: string;
  bestMatch?: {
    testCaseId: number;
    title: string;
    similarity: number;
    relevant: boolean;
  };
}
export interface CoveredMethod {
  method: string;
  module: string;
  filePath: string;
  changeType: string;
  status: 'COVERED';
  matchingAdoTestCases: AdoTestCase[];
  generatedTestCases: CoveredSuggestedTestCase[];
}

export interface NotCoveredMethod {
  method: string;
  filePath: string;
  changeType: string;
  status: 'NOT COVERED';
  suggestedTestCases: AdoTestCase[];
  module: string;
}

export interface GapAnalysisSummary {
  totalMethods: number;
  covered: number;
  notCovered: number;
  coveragePercentage: number;
}

export interface GapAnalysisResponse {
  summary: GapAnalysisSummary;
  covered: CoveredMethod[];
  notCovered: NotCoveredMethod[];
}

// ── Request types ─────────────────────────────────────────────────────────────

interface TestPlansRequest {
  organization: string;
  project: string;
  _pat: string;
}

interface TestSuiteDetailRequest {
  organization: string;
  project: string;
  planId: string;
  suiteId: number;
  _pat: string;
}

export interface IngestRequest {
  organization: string;
  project: string;
  planId: string;
  suiteIds: string[];
  _pat: string;
}

export interface GenerateRequest {
  ingestionId: string;
  diffResult: CompareResponse & { additionalProp1: Record<string, never> };
  repositoryUrl: string;
  oldCommit: string;
  newCommit: string;
  buildANumber: string;
  buildBNumber: string;
  extraContext: string;
  _pat: string;
}

interface AnalyzeGapRequest {
  generationId: string;
  similarityThreshold: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const patHeaders = (pat: string) => ({
  headers: { 'Requester-Token': pat, 'Content-Type': 'application/json' },
});

function stripPat<T extends { _pat: string }>(
  params: T,
): { body: Omit<T, '_pat'>; pat: string } {
  const { _pat, ...body } = params;
  return { body, pat: _pat };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export const useGetTestPlansMutation = () =>
  useMutation<{ plans: TestPlan[] }, Error, TestPlansRequest>({
    mutationFn: params => {
      const { body, pat } = stripPat(params);
      return api.post(
        '/api/test-intelligence/test-plans',
        body,
        patHeaders(pat),
      );
    },
  });

export const useGetTestSuiteDetailMutation = () =>
  useMutation<{ suite: TestSuite }, Error, TestSuiteDetailRequest>({
    mutationFn: params => {
      const { body, pat } = stripPat(params);
      return api.post(
        '/api/test-intelligence/test-suites/detail',
        body,
        patHeaders(pat),
      );
    },
  });

export const useIngestTestCasesMutation = () =>
  useMutation<{ ingestionId: string }, Error, IngestRequest>({
    mutationFn: params => {
      const { body, pat } = stripPat(params);
      return api.post('/api/test-intelligence/ingest', body, patHeaders(pat));
    },
  });

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraCredentials {
  jiraEmail: string;
  jiraApiToken: string;
}

export interface JiraIngestRequest extends JiraCredentials {
  organization: string;
  project: string;
}

export const useJiraProjectsMutation = () =>
  useMutation<
    { projects: JiraProject[] },
    Error,
    JiraCredentials & { organization: string }
  >({
    mutationFn: body => api.post('/api/test-intelligence/jira/projects', body),
  });

export const useIngestJiraTestCasesMutation = () =>
  useMutation<{ ingestionId: string }, Error, JiraIngestRequest>({
    mutationFn: body => api.post('/api/test-intelligence/jira/ingest', body),
  });

export const useGenerateMappingMutation = () =>
  useMutation<{ generationId: string }, Error, GenerateRequest>({
    mutationFn: params => {
      const { body, pat } = stripPat(params);
      return api.post('/api/test-intelligence/generate', body, patHeaders(pat));
    },
  });

export const useAnalyzeGapMutation = () =>
  useMutation<GapAnalysisResponse, Error, AnalyzeGapRequest>({
    mutationFn: body => api.post('/api/test-intelligence/analyze-gap', body),
  });

// ── Polling queries ───────────────────────────────────────────────────────────

export const useIngestionStatus = (ingestionId: string, enabled: boolean) =>
  useQuery<StatusResponse>({
    queryKey: ['ingestion-status', ingestionId],
    queryFn: () => api.get(`/api/test-intelligence/ingestion/${ingestionId}`),
    enabled: enabled && !!ingestionId,
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'processing' ? 3000 : false;
    },
  });

export const useGenerationStatus = (
  generationId: string | null,
  enabled: boolean,
) =>
  useQuery<StatusResponse>({
    queryKey: ['generation-status', generationId],
    queryFn: () => api.get(`/api/test-intelligence/generation/${generationId}`),
    enabled: enabled && !!generationId,
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'processing' ? 3000 : false;
    },
  });
